import React from 'react';
import Button from '@hero/ui-kit/inputs/Button';
import Tooltip from '@hero/ui-kit/components/Tooltip';
import Container from '@hero/ui-kit/layout/Container';
import P from '@hero/ui-kit/typography/P';

import Table from '../../../components/Table';
import {
    dateTime,
    baseCodeFor,
    remainingReviewKinds,
    enrollmentDate,
    enrollmentPeriods,
    patientEnrollmentEndings,
    patientCancellationDates,
    terminationExplanation,
    duration,
    timeText,
    RowFilter,
    rowTone,
    codePresentation,
    rowNotes,
    rowExplanation
} from '../presentation';
import { patientLabel, patientTableRows } from '../rowSets';
import { careCsv, downloadCareCsv } from '../csv';
import * as Style from '../style.module.scss';
import { ActionTarget, CareRow, CareRows } from '../types';
import RowActions from './RowActions';
import PatientConditions from './PatientConditions';
import RelatedConditions from './RelatedConditions';

type Props = {
    month: string;
    organizationId?: string | number;
    downloadDisabledReason?: string;
    rows?: CareRows;
    filter: RowFilter;
    canWrite: boolean;
    actionsDisabled: boolean;
    onAction: (target: ActionTarget) => void;
    onEvidence: (row: CareRow) => void;
    onHistory: (row: CareRow) => void;
};

const CancellationBadge: React.FC<{ dates: string[] }> = ({ dates }) => {
    if (!dates.length) return null;
    const label = `Membership cancelled: ${dates.join('; ')}`;
    return (
        <span className={Style.cancellationIcon} role="img" aria-label={label} title={label} tabIndex={0}>
            <svg
                width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                aria-hidden="true" focusable="false"
            >
                <circle cx="12" cy="12" r="9" />
                <path d="m6 6 12 12" />
            </svg>
        </span>
    );
};

const RowsTable: React.FC<Props> = ({
    month,
    organizationId,
    downloadDisabledReason,
    rows,
    filter,
    canWrite,
    actionsDisabled,
    onAction,
    onEvidence,
    onHistory
}) => {
    const table = patientTableRows(rows, filter);
    const {
        visible, patients, allPatientCodes, changedPatients, rowsById,
        sortedPatients, endedPatients, sortedEndedPatients, endedEnrollmentCount
    } = table;
    const downloadReason = organizationId == null ? 'Select an organization before downloading.'
        : downloadDisabledReason ? downloadDisabledReason
        : !rows ? 'Patient rows are loading.'
        : rows.rows.length !== rows.total_rows ? 'Some patient rows are not included.'
        : (visible?.length ?? 0) + endedEnrollmentCount === 0 ? 'No rows to download.'
        : undefined;
    const canDownload = downloadReason === undefined;
    const download = () => {
        if (!canDownload || !rows || organizationId == null) return;
        downloadCareCsv(careCsv(table, month, organizationId, rows.termination_records, rows.termination_evidence), month, organizationId);
    };
    const columns = [
        'Patient',
        'Program / CPT code',
        'Outcome / units',
        'Why',
        ...(canWrite ? ['Actions'] : [])
    ];
    const grid = `minmax(18rem, 1.5fr) minmax(18rem, 1.3fr) minmax(13rem, 1fr) minmax(24rem, 2fr)${canWrite ? ' minmax(20rem, 1.5fr)' : ''}`;

    return (
        <div className={Style.tableScroll}>
            <div className={`${Style.patientTable}${canWrite ? ` ${Style.patientTableWritable}` : ''}`}>
                <Table
                    title={
                        <span className={Style.patientTableHeading}>
                            <span>
                                {visible
                                    ? `Patients (${patients.size}) · Codes (${visible.length} of ${rows?.total_rows})${endedEnrollmentCount ? ` · Ended enrollments (${endedEnrollmentCount})` : ''}`
                                    : 'Patients'}
                            </span>
                            <span className={Style.patientTableHint}>Calculated from provider minutes</span>
                            <Tooltip content={downloadReason ?? 'Download CSV of the filtered patient table'}>
                                <span tabIndex={downloadReason ? 0 : undefined}>
                                    <Button
                                        size="small"
                                        variant="outlineSecondary"
                                        width="natural"
                                        noDefaultMargin
                                        disabled={!canDownload}
                                        onClick={download}
                                    >
                                        Download CSV
                                    </Button>
                                </span>
                            </Tooltip>
                        </span>
                    }
                    headerColumns={columns}
                    headerGridTemplateColumns={grid}
                    loading={!rows}
                    size="compact"
                >
                    {[
                        ...sortedPatients.map(([userId, codes]) => {
                            const patient = codes[0];
                            const changedRow = changedPatients.get(userId);
                            return (
                                <Container
                                    key={userId}
                                    gridTemplateColumns={grid}
                                    className={Style.patientGroup}
                                >
                                    <div className={Style.patientIdentity}>
                                        <div className={Style.patientHeading}>
                                            <strong>{patientLabel(patient)}</strong>
                                            <CancellationBadge dates={patientCancellationDates(
                                                allPatientCodes.get(userId) ?? codes,
                                                month, rows?.termination_records ?? [], userId
                                            )} />
                                            {changedRow ? (
                                                <button
                                                    type="button"
                                                    className={Style.changedIcon}
                                                    aria-label={`Changed this month. View ${changedRow.program} ${changedRow.code} history for ${patientLabel(patient)}. Other changed codes can be selected in the dialog.`}
                                                    title={
                                                        changedRow.changes.changed_at
                                                            ? `Changed this month — view ${changedRow.program} ${changedRow.code} history. Last changed: ${dateTime(changedRow.changes.changed_at)}. Select other changed codes in the dialog.`
                                                            : `Changed this month — view ${changedRow.program} ${changedRow.code} history. Select other changed codes in the dialog.`
                                                    }
                                                    onClick={() => onHistory(changedRow)}
                                                >
                                                    <svg
                                                        width="18"
                                                        height="18"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        aria-hidden="true"
                                                        focusable="false"
                                                    >
                                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                                        <path d="M3 3v5h5M12 7v5l4 2" />
                                                    </svg>
                                                </button>
                                            ) : null}
                                        </div>
                                        {patientEnrollmentEndings(
                                            allPatientCodes.get(userId) ?? codes,
                                            month,
                                            rows?.termination_records ?? []
                                        ).map((ending) => (
                                            <P key={ending} size="small" noDefaultMargin>
                                                {ending}
                                            </P>
                                        ))}
                                        <PatientConditions rows={allPatientCodes.get(userId) ?? codes} />
                                    </div>
                                    <div className={Style.patientCodes}>
                                        {codes.map((row) => {
                                            const periods = enrollmentPeriods(row, rows?.termination_records ?? []);
                                            const moreChecks = remainingReviewKinds(row).length;
                                            const notes = rowNotes(row);
                                            const { reportedOnly, seconds, status, timeBasis, units } = codePresentation(row);
                                            return (
                                                <div
                                                    key={row.charge_id}
                                                    className={`${Style.patientCode} ${Style[`billing_${rowTone(row)}`]}`}
                                                >
                                                    <div className={Style.codeDetails}>
                                                        <div className={Style.codeHeading}>
                                                            <div className={Style.codeSummary}>
                                                                <div className={Style.codeIdentity}>
                                                                    <span className={Style.codeProgram}>{row.program}</span>
                                                                    <strong>{row.code}</strong>
                                                                </div>
                                                                <div className={Style.codeMinutes}>
                                                                    <strong
                                                                        aria-label={timeBasis === 'Unavailable' ? 'Care time unavailable' : `${timeBasis} care time: ${timeText(seconds)}`}
                                                                        title={timeBasis === 'Unavailable' ? 'Calculate billable codes to update care time' : reportedOnly ? 'Time reported in the CSV; enrollment is missing' : 'Care time counted for this code'}
                                                                    >
                                                                        {seconds == null ? 'Unavailable' : duration(seconds)}
                                                                    </strong>
                                                                    {timeBasis === 'Reported' ? <span className={Style.codeProgram}> reported</span> : null}
                                                                </div>
                                                            </div>
                                                            <span className={Style.codeMinimum}>
                                                                Minimum: {row.required_seconds == null ? 'Unavailable' : duration(row.required_seconds)}
                                                            </span>
                                                        </div>
                                                        <RelatedConditions row={row} patientRows={allPatientCodes.get(userId) ?? codes} />
                                                        {periods.length ? (
                                                            periods.map((period) => (
                                                                <div className={Style.enrollmentPeriod} key={`${period.enrolledAt}:${period.disenrolledAt}`}>
                                                                    {period.enrolledAt ? (
                                                                        <P size="small" noDefaultMargin>
                                                                            Enrolled:{' '}
                                                                            {enrollmentDate(period.enrolledAt)}
                                                                        </P>
                                                                    ) : null}
                                                                    {period.disenrolledAt ? (
                                                                        <P size="small" noDefaultMargin>
                                                                            Disenrolled:{' '}
                                                                            {enrollmentDate(period.disenrolledAt)}
                                                                        </P>
                                                                    ) : null}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <P size="small" noDefaultMargin>
                                                                {reportedOnly ? 'No enrollment found for this month' : 'Enrollment dates not recorded'}
                                                            </P>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <strong>
                                                            {status}
                                                        </strong>
                                                        <P size="small" noDefaultMargin>
                                                            {units}
                                                        </P>
                                                    </div>
                                                    <div>
                                                        <P size="small" noDefaultMargin>
                                                            {rowExplanation(row, month, baseCodeFor(row, rowsById))}
                                                        </P>
                                                        <div className={Style.codeActions}>
                                                            <Button
                                                                size="small"
                                                                variant="outlineSecondary"
                                                                width="natural"
                                                                noDefaultMargin
                                                                aria-label={moreChecks
                                                                    ? `View ${moreChecks} more review ${moreChecks === 1 ? 'check' : 'checks'} for ${row.program} ${row.code}`
                                                                    : `View evidence for ${row.program} ${row.code}`}
                                                                onClick={() => onEvidence(row)}
                                                            >
                                                                {moreChecks
                                                                    ? `${moreChecks} more ${moreChecks === 1 ? 'check' : 'checks'}`
                                                                    : 'View evidence'}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlineSecondary"
                                                                width="natural"
                                                                noDefaultMargin
                                                                onClick={() => onHistory(row)}
                                                            >
                                                                History
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    {canWrite ? (
                                                        <RowActions
                                                            row={row}
                                                            disabled={actionsDisabled}
                                                            onAction={onAction}
                                                        />
                                                    ) : null}
                                                    {notes.length ? (
                                                        <div className={Style.codeNotes}>
                                                            <P size="small" strong noDefaultMargin>
                                                                Notes · {row.program} {row.code}
                                                            </P>
                                                            {notes.map((note) => (
                                                                <P key={note} size="small" noDefaultMargin>
                                                                    {note}
                                                                </P>
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Container>
                            );
                        }),
                        ...sortedEndedPatients.map(([userId, records]) => (
                            <Container
                                key={`ended:${userId}`}
                                gridTemplateColumns={grid}
                                className={Style.patientGroup}
                            >
                                <div className={Style.patientIdentity}>
                                    <div className={Style.patientHeading}>
                                        <strong>{patientLabel({ patient_name: null, hero_user_id: userId })}</strong>
                                        <CancellationBadge dates={patientCancellationDates([], month, records, userId)} />
                                    </div>
                                </div>
                                <div className={Style.patientCodes}>
                                    {records.map((record) => (
                                        <div
                                            key={`${record.program}:${record.episode_id}`}
                                            className={`${Style.patientCode} ${Style.billing_unknown}`}
                                        >
                                            <div>
                                                <P strong noDefaultMargin>{record.program}</P>
                                                <P size="small" noDefaultMargin>
                                                    Enrolled: {enrollmentDate(record.enrolled_at)}
                                                </P>
                                                <P size="small" noDefaultMargin>
                                                    Ended: {enrollmentDate(record.terminated_at)}
                                                </P>
                                            </div>
                                            <div>
                                                <strong>No care codes</strong>
                                                <P size="small" noDefaultMargin>Information only</P>
                                            </div>
                                            <div>
                                                <P size="small" noDefaultMargin>
                                                    {terminationExplanation(record)}
                                                </P>
                                            </div>
                                            {canWrite ? <div /> : null}
                                        </div>
                                    ))}
                                </div>
                            </Container>
                        )),
                        ...(visible?.length === 0 && endedPatients.size === 0
                            ? [
                                  <P key="empty" noDefaultMargin>
                                      No patient codes match these filters.
                                  </P>
                              ]
                            : []),
                        <P key="explanation" size="small" noDefaultMargin>
                            Times are minutes:seconds. Each code uses the same monthly program total; do not add the rows together.
                        </P>
                    ]}
                </Table>
            </div>
        </div>
    );
};

export default RowsTable;
